<template>
  <header>
    <HomeHero v-if="isHomePage"/>

    <section
        v-else-if="hasHeader"
        :style="headerBackgroundStyle"
        class="relative h-[calc(100svh-4rem)] sm:h-auto sm:aspect-5/2 bg-cover bg-center bg-no-repeat xl:bg-center"
    >
      <!--      <BrandWave-->
      <!--        :height="80"-->
      <!--        class="absolute left-0 top-0 z-10 w-full pointer-events-none"-->
      <!--        flip-->
      <!--        variant="duo"-->
      <!--      />-->

      <span class="noprint"/>

      <BrandWave
          :height="150"
          class="absolute bottom-0 left-0 z-10 w-full pointer-events-none"
          variant="trio"
      />
      <button
          v-if="currentRouteName === 'playgroundConfigurator'"
          class="absolute top-20 right-64 w-40 h-40 rounded-full bg-secondary hover:bg-secondary/90 transition-colors shadow-lg flex items-center justify-center text-white text-lg font-bold text-center p-2 cursor-pointer leading-tight"
          @click="scrollToGuestSection"
      >
        🎉 <br>Jetzt Neu - <br>Planen Sie Ihren Spielplatz
      </button>

      <div
          v-if="hasTitle"
          class="title"
      >
        <div class="mx-auto flex w-fit max-w-full flex-col">
          <div class="flex w-fit max-w-full flex-col items-center gap-3 relative">
            <div class="inline-flex w-fit max-w-full flex-col items-center sm:items-start">
              <span class="animate-ltr inline-block w-fit max-w-full rounded-md">
                {{ header.title }}
              </span>

              <span
                  v-if="header.subTitle"
                  class="animate-rtl inline-block w-fit max-w-full rounded-md"
              >
                {{ header.subTitle }}
              </span>
            </div>

            <p
                v-if="header.text"
                class="max-w-3xl bg-secondary/80 px-3 py-2 text-sm leading-relaxed text-white lg:px-4 lg:py-3 lg:text-base"
                v-html="header.text"
            />
          </div>
        </div>
      </div>

      <div
          v-if="videoButtons.length"
          class="absolute top-[80%] z-10 flex w-full justify-center gap-4"
      >
        <UButton
            v-for="button in videoButtons"
            :key="button.hash"
            :color="button.color"
            :to="button.hash"
            :ui="{ base: 'text-xs lg:text-lg' }"
            variant="solid"
        >
          <IconPlay/>
          <span class="hidden lg:inline">Video: </span>{{ button.label }}
        </UButton>
      </div>
    </section>
  </header>
</template>

<script lang="ts" setup>
import MainNavigationNew from '~/components/Header/MainNavigationNew.vue'
import HomeHero from '~/components/Header/HomeHero.vue'
import IconPlay from '~/components/Icons/IconPlay.vue'

type HeaderState = {
  title: string
  subTitle: string
  text: string
}

type HeaderRouteName =
    | 'categories'
    | 'category'
    | 'search'
    | 'data-protection'
    | 'imprint'
    | 'gtc'
    | 'material'
    | 'playgrounds'
    | 'slides'
    | 'melamini'
    | 'climbing'
    | 'waterFacilities'
    | 'metall'
    | 'manufacturing'
    | 'service'
    | 'collaboration'
    | 'certification'
    | 'warranty'
    | 'downloads'
    | 'contact'
    | 'inclusion'
    | 'montage'
    | 'blog'
    | 'blog-post'
    | 'sustainability'
    | 'playgroundConfigurator'
    | 'arApp'
    | 'career'
    | 'newsletter'

type ButtonColor = 'primary' | 'secondary'

type VideoButton = {
  label: string
  hash: `#${string}`
  color: ButtonColor
}

const props = defineProps<{
  imgSrc?: string
}>()

const route = useRoute()

const header = useState<HeaderState>('header', () => ({
  title: '',
  subTitle: '',
  text: ''
}))

const headerBackgrounds: Partial<Record<HeaderRouteName, string>> = {
  gtc: '/images/backgrounds/BQ4C2202-1.jpg',
  material: '/images/backgrounds/BQ4C1368-1.jpg',
  playgrounds: '/images/backgrounds/BQ4C0483.jpg',
  slides: '/images/backgrounds/BQ4C2049.jpg',
  melamini: '/images/backgrounds/2232743_3.jpg',
  climbing: '/images/backgrounds/BQ4C6158_neu.jpg',
  waterFacilities: '/images/backgrounds/BQ4C5954.jpg',
  metall: '/images/backgrounds/material-metall-4-0-header.png',
  manufacturing: '/images/backgrounds/BQ4C2202-1.jpg',
  service: '/images/backgrounds/BQ4C0119-1.jpg',
  collaboration: '/images/backgrounds/BQ4C0119-1.jpg',
  certification: '/images/backgrounds/BQ4C6323neu.jpg',
  warranty: '/images/backgrounds/BQ4C1947.jpg',
  downloads: '/images/backgrounds/BQ4C2202-1.jpg',
  contact: '/images/backgrounds/BQ4C2202-1.jpg',
  inclusion: '/images/backgrounds/IMG_3795.jpg',
  montage: '/images/backgrounds/Montage.jpg',
  blog: '/images/backgrounds/BQ4C0119-1.jpg',
  sustainability: '/images/backgrounds/header-sustainability.jpg',
  playgroundConfigurator: '/images/backgrounds/header-configurator.png',
  arApp: '/images/backgrounds/header-3d-planer-app.jpg',
  career: '/images/backgrounds/BQ4C2379.jpg',
  newsletter: '/images/backgrounds/BQ4C2202-1.jpg'
}

const pageHeaderRoutes = new Set<HeaderRouteName>([
  'categories',
  'category',
  'search',
  'data-protection',
  'imprint',
  'gtc',
  'material',
  'playgrounds',
  'slides',
  'melamini',
  'climbing',
  'waterFacilities',
  'metall',
  'manufacturing',
  'service',
  'collaboration',
  'certification',
  'warranty',
  'downloads',
  'contact',
  'inclusion',
  'montage',
  'blog',
  'blog-post',
  'sustainability',
  'playgroundConfigurator',
  'arApp',
  'career',
  'newsletter'
])

const videoButtonConfig: Partial<Record<HeaderRouteName, VideoButton[]>> = {
  playgroundConfigurator: [
    {label: 'Einleitung', hash: '#video-193', color: 'secondary'},
    {label: 'Technische Vorstellung', hash: '#video-188', color: 'secondary'}
  ],
  arApp: [
    {label: 'Einleitung', hash: '#video-459', color: 'primary'},
    {label: 'Funktionen', hash: '#video-458', color: 'primary'}
  ]
}

const currentRouteName = computed(() => String(route.name ?? '') as HeaderRouteName)

const isHomePage = computed(() => currentRouteName.value === 'index')

const hasHeader = computed(() => pageHeaderRoutes.has(currentRouteName.value))

const hasTitle = computed(() => Boolean(header.value.title))

const headerBackgroundStyle = computed(() => {
  const image = props.imgSrc || headerBackgrounds[currentRouteName.value] || '/images/backgrounds/BQ4C1421-1.jpg'

  return {
    backgroundImage: `url(${image})`
  }
})

const videoButtons = computed<VideoButton[]>(() => videoButtonConfig[currentRouteName.value] ?? [])

function scrollToGuestSection() {
  const element = document.getElementById('guest-version-teaser')
  if (element) {
    element.scrollIntoView({behavior: 'smooth', block: 'nearest'})
  }
}
</script>
